/**
 * Agent Context Menu Router
 *
 * Centralizes dispatch of context menu actions for all agent node types
 * (jobs, operators, alerts, proxies, subfolders). Extracted from DatabaseExplorer
 * to prevent the main explorer component from growing with each new agent type.
 */

import type { TreeNode } from '../../explorer/models/TreeModel';
import type {
  AgentJobNodeMetadata,
  AgentOperatorNodeMetadata,
  AgentAlertNodeMetadata,
  AgentProxyNodeMetadata,
} from '../../explorer/models/nodeTypes';
import type { JobSchedulerSubView } from '../../workspace/core/types/workspaceTypes';
import { executeAgentJobContextMenuAction } from './agentJobContextMenuService';
import { executeAgentOperatorContextMenuAction } from './agentOperatorContextMenuService';
import { executeAgentAlertContextMenuAction } from './agentAlertContextMenuService';
import { executeAgentProxyContextMenuAction } from './agentProxyContextMenuService';
import { openAgentWorkspace } from './agentContextMenuHelper';

const AGENT_NODE_TYPES = new Set([
  'agent-job', 'agent-operator', 'agent-alert', 'agent-proxy', 'agent-jobs-folder', 'agent-jobs-subfolder',
]);

const SUBFOLDER_NAME_TO_SUBVIEW: Record<string, JobSchedulerSubView> = {
  jobs: 'jobs',
  operators: 'operators',
  alerts: 'alerts',
  proxies: 'proxies',
  'error logs': 'error-logs',
};

/** Returns true if the node type is an agent node handled by this router */
export function isAgentNodeType(type: string): boolean {
  return AGENT_NODE_TYPES.has(type);
}

/** Dispatches context menu actions for agent node types. Returns true if handled. */
export function executeAgentContextMenuAction(
  actionId: string,
  node: TreeNode,
  callbacks: { onRefresh: () => void },
): boolean {
  if (node.type === 'agent-job') {
    const meta = node.metadata as AgentJobNodeMetadata | undefined;
    if (meta) executeAgentJobContextMenuAction(actionId, meta, callbacks);
    return true;
  }

  if (node.type === 'agent-operator') {
    const meta = node.metadata as AgentOperatorNodeMetadata | undefined;
    if (meta) executeAgentOperatorContextMenuAction(actionId, meta, callbacks);
    return true;
  }

  if (node.type === 'agent-alert') {
    const meta = node.metadata as AgentAlertNodeMetadata | undefined;
    if (meta) executeAgentAlertContextMenuAction(actionId, meta, callbacks);
    return true;
  }

  if (node.type === 'agent-proxy') {
    const meta = node.metadata as AgentProxyNodeMetadata | undefined;
    if (meta) executeAgentProxyContextMenuAction(actionId, meta, callbacks);
    return true;
  }

  if (node.type === 'agent-jobs-folder' && actionId === 'open-job-scheduler') {
    const connId = (node.metadata as { connectionId?: string })?.connectionId;
    if (connId) openAgentWorkspace(connId, 'jobs');
    return true;
  }

  if (node.type === 'agent-jobs-subfolder' && actionId === 'open-job-scheduler') {
    const connId = (node.metadata as { connectionId?: string })?.connectionId;
    if (connId) {
      const subView = SUBFOLDER_NAME_TO_SUBVIEW[node.name?.toLowerCase() ?? ''] ?? 'jobs';
      openAgentWorkspace(connId, subView);
    }
    return true;
  }

  return false;
}
